/**
 * PDF export for the employee timeline ("Carnet de bord RH").
 * One PDF with a header (employee name, period) and a single autoTable of entries.
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * @param {Object} opts
 * @param {{name: string}} opts.employee
 * @param {string} opts.periodLabel e.g. "Année 2026" or "01/01/2024 → 31/12/2025"
 * @param {Array<{date, time?, label, title, description}>} opts.entries (sorted desc)
 */
export function generateTimelinePdf({ employee, periodLabel, entries }) {
  const pdf = new jsPDF({ unit: "mm", format: "a4" });

  // Header
  pdf.setFontSize(16);
  pdf.setFont("helvetica", "bold");
  pdf.text("Carnet de bord RH", 14, 16);

  pdf.setFontSize(11);
  pdf.text(`${employee?.name || ""}`, 14, 24);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.text(`Période : ${periodLabel}`, 14, 30);
  pdf.text(`${entries.length} entrée(s)`, 14, 35);
  pdf.text(`Édité le ${new Date().toLocaleDateString("fr-FR")}`, 14, 40);

  // Table
  const statusLabels = {
    over: "Dépassement",
    slight_over: "Léger dépass.",
    on_target: "À l'équilibre",
    slight_under: "Légère avance",
    under: "Gain de temps",
    no_target: "Sans cible",
  };
  const body = entries.map((ev) => {
    let details = ev.description || "";
    if (ev.category === "chantier" && ev.meta?.time_delta) {
      const td = ev.meta.time_delta;
      const sign = td.delta_hours > 0 ? "+" : "";
      const label = statusLabels[td.status] || td.status || "";
      details += `\n⏱ ${label} : ${sign}${td.delta_hours} h (${sign}${td.delta_percent}%) — réel ${td.actual_hours} h / théorique ${td.theoretical_hours} h`;
    }
    return [
      ev.date ? new Date(ev.date).toLocaleDateString("fr-FR") : "—",
      ev.time || "",
      ev.label || "",
      ev.title || "",
      details,
    ];
  });

  autoTable(pdf, {
    startY: 48,
    head: [["Date", "Heure", "Type", "Titre", "Détails"]],
    body,
    styles: { fontSize: 8, cellPadding: 2, overflow: "linebreak" },
    headStyles: { fillColor: [45, 55, 72], textColor: 255 },
    columnStyles: {
      0: { cellWidth: 22 },
      1: { cellWidth: 14 },
      2: { cellWidth: 32 },
      3: { cellWidth: 45 },
      4: { cellWidth: "auto" },
    },
    didDrawPage: (data) => {
      const pageCount = pdf.internal.getNumberOfPages();
      const currentPage = data.pageNumber;
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "normal");
      pdf.text(
        `Page ${currentPage} / ${pageCount}`,
        pdf.internal.pageSize.getWidth() - 25,
        pdf.internal.pageSize.getHeight() - 8
      );
    },
  });

  const safeName = (employee?.name || "salarie").replace(/[^a-zA-Z0-9\-_]+/g, "_");
  pdf.save(`Carnet_bord_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`);
}

export default generateTimelinePdf;
